import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { makeQrValue } from '../utils/qrCodes';
import { decodeHtml } from '../utils/decodeHtml';
import { generateAndPrintLabels, generateAndDownloadLabels } from '../utils/printLabel';
import { resolveDisplayNames, saveShortNames, useLabelPrefsSync } from '../utils/labelNames';
import LabelEditor from './LabelEditor';
import useScrollLock from '../utils/useScrollLock';
import ReprintDamagedModal from './ReprintDamagedModal';
import './QRCodeDisplay.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export function QRModal({ open, onClose, productId, variationId, productName, variationLabel, sku, price, storeName, labelConfig }) {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const [activeLots, setActiveLots] = useState(null);
  const [selectedLoteNum, setSelectedLoteNum] = useState('');
  const [reprintOpen, setReprintOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editVar,  setEditVar]  = useState('');
  // Seeds para computeLayout (sin sliders — se usan solo para init del editor)
  const [seeds, setSeeds] = useState({ qr: 1, priceSize: 9, nameSize: 4, varSize: 3.5 });
  // Layout absoluto generado por el editor drag
  const [editLayout, setEditLayout] = useState(null);
  const [savedFeedback, setSavedFeedback] = useState(false);
  const editDataRef = useRef({});
  // Sincroniza las preferencias de etiqueta con el servidor (cross-dispositivo).
  // Al llegar datos frescos, prefsVer cambia y re-resolvemos nombre/variación/layout.
  const prefsVer = useLabelPrefsSync();
  useScrollLock(open);

  useEffect(() => {
    if (!savedFeedback) return;
    const t = setTimeout(() => setSavedFeedback(false), 1500);
    return () => clearTimeout(t);
  }, [savedFeedback]);

  useEffect(() => {
    if (!open) return;
    const origName = decodeHtml(productName) || '';
    const origVar  = decodeHtml(variationLabel) || '';
    const resolved = resolveDisplayNames(productId, variationId, origName, origVar);
    setEditName(resolved.name);
    setEditVar(resolved.variation);
    setEditLayout(resolved.layout);
    setSeeds({ qr: resolved.qr, priceSize: resolved.priceSize, nameSize: resolved.nameSize, varSize: resolved.varSize });
  }, [open, productId, variationId, productName, variationLabel, prefsVer]);

  useEffect(() => {
    if (!open || !productId) { setActiveLots(null); setSelectedLoteNum(''); return; }
    let cancelled = false;
    const params = new URLSearchParams({ productId: String(productId) });
    if (variationId) params.set('variationId', String(variationId));
    fetch(`${API_URL}/api/lots/active?${params}`)
      .then((r) => r.ok ? r.json() : { lots: [] })
      .then((data) => {
        if (cancelled) return;
        const lots = data.lots || [];
        setActiveLots(lots);
        if (lots.length > 0) setSelectedLoteNum(lots[0].loteNumero);
      })
      .catch(() => { if (!cancelled) setActiveLots([]); });
    return () => { cancelled = true; };
  }, [open, productId, variationId]);

  // Ref siempre actualizado para que el callback no tenga deps y sea estable
  editDataRef.current = { productId, variationId, editName, editVar, productName, variationLabel };

  const handleLayoutChange = useCallback((layout) => {
    setEditLayout(layout);
    setSavedFeedback(true);
    const d = editDataRef.current;
    saveShortNames(d.productId, d.variationId, {
      name: d.editName, variation: d.editVar,
      layout,
      originalName: decodeHtml(d.productName),
      originalVariation: decodeHtml(d.variationLabel),
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const name     = decodeHtml(productName);
  const varLabel = decodeHtml(variationLabel);
  const qrValue  = makeQrValue(productId, variationId);
  const displayName = varLabel ? `${name} — ${varLabel}` : name;

  const currentLot = activeLots?.find((l) => l.loteNumero === selectedLoteNum) || activeLots?.[0] || null;

  const getQrDataUrl = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    return canvas ? canvas.toDataURL('image/png') : null;
  };

  const labelItem = (qrDataUrl) => ({
    qrDataUrl,
    productName:    (editName || '').trim() || name,
    variationLabel: (editVar  || '').trim() || null,
    sku, price, storeName,
    loteNumero: currentLot?.loteNumero || null,
    layout: editLayout,
    // Fallback seeds (usados si layout es null)
    qrScale:   seeds.qr,
    priceSize: seeds.priceSize,
    nameSize:  seeds.nameSize,
    varSize:   seeds.varSize,
    productId, variationId,
  });

  const persistShort = () => {
    saveShortNames(productId, variationId, {
      name: editName, variation: editVar,
      layout: editLayout,
      originalName: name, originalVariation: varLabel,
    });
  };

  const handlePrint = () => {
    const qrDataUrl = getQrDataUrl();
    if (!qrDataUrl) return;
    setBusy('print');
    try { persistShort(); generateAndPrintLabels([labelItem(qrDataUrl)], labelConfig); }
    finally { setBusy(null); }
  };

  const handleDownload = () => {
    const qrDataUrl = getQrDataUrl();
    if (!qrDataUrl) return;
    setBusy('download');
    try { persistShort(); generateAndDownloadLabels([labelItem(qrDataUrl)], labelConfig); }
    finally { setBusy(null); }
  };

  // Item para el editor (refleja los textos editados en tiempo real)
  const editorItem = {
    qrValue,
    productName:    (editName || '').trim() || name,
    variationLabel: (editVar || varLabel || '').trim() || null,
    price, sku,
    loteNumero: currentLot?.loteNumero || null,
    storeName,
    // Seeds para computeLayout en el primer render
    qrScale:   seeds.qr,
    priceSize: seeds.priceSize,
    nameSize:  seeds.nameSize,
    varSize:   seeds.varSize,
  };

  const modal = (
    <div className="qrm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="qrm-card" role="dialog" aria-modal="true">
        <div className="qrm-head">
          <span className="qrm-head__title">Editar etiqueta</span>
          <button className="qrm-close" onClick={onClose} aria-label="Cerrar">×</button>
        </div>

        {/* Canvas oculto: fuente del PNG del QR para el PDF */}
        <span ref={canvasRef} className="qrm-canvas-hidden" aria-hidden="true">
          <QRCodeCanvas value={qrValue} size={400} level="M" includeMargin={false} />
        </span>

        <div className="qrm-body">

        {/* Editor interactivo drag */}
        <div className="qrm-editor">
          <LabelEditor
            key={`le-${productId}-${variationId}`}
            item={editorItem}
            config={labelConfig}
            initialLayout={editLayout}
            onLayoutChange={handleLayoutChange}
            maxWidthPx={260}
          />
          <div className="qrm-editor__captions">
            <span className="qrm-editor__preview-cap">{displayName}</span>
            {savedFeedback && <span className="qrm-saved-badge">✓ Guardado</span>}
          </div>
        </div>

        {/* Campos de texto */}
        <div className="qrm-editor__fields">
          <label className="qrm-editor__field">
            <span className="qrm-editor__label">Nombre a mostrar</span>
            <input
              className="qrm-editor__input"
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder={name}
            />
          </label>
          {varLabel && (
            <label className="qrm-editor__field">
              <span className="qrm-editor__label">Variación a mostrar</span>
              <input
                className="qrm-editor__input"
                type="text"
                value={editVar}
                onChange={(e) => setEditVar(e.target.value)}
                placeholder={varLabel}
              />
            </label>
          )}
          <p className="qrm-editor__hint">
            Acorta el texto · toca el QR o el texto y arrastra para moverlos · arrastra la esquina azul/morada para cambiar tamaño.
            Los cambios se guardan al generar.
          </p>
        </div>

        {/* Lote */}
        {activeLots !== null && activeLots.length > 1 && (
          <div className="qrm-lot-row">
            <label className="qrm-lot-label" htmlFor="qrm-lot-sel">Lote para la etiqueta:</label>
            <select id="qrm-lot-sel" className="qrm-lot-select" value={selectedLoteNum} onChange={(e) => setSelectedLoteNum(e.target.value)}>
              {activeLots.map((l) => (
                <option key={l.loteNumero} value={l.loteNumero}>{l.loteNumero} · {l.quantityRemaining} restantes</option>
              ))}
            </select>
          </div>
        )}
        {activeLots !== null && activeLots.length === 1 && (
          <div className="qrm-lot-badge">
            Lote: <strong>{activeLots[0].loteNumero}</strong>
            <span> · {activeLots[0].quantityRemaining} restantes</span>
          </div>
        )}

        {currentLot && (
          <div className="qrm-reprint-row">
            <button className="qrm-btn qrm-btn--ghost" onClick={() => setReprintOpen(true)}>
              Reimprimir etiqueta dañada
            </button>
          </div>
        )}

        </div>{/* end qrm-body */}
        <div className="qrm-actions">
          <button className="qrm-btn qrm-btn--soft" onClick={onClose}>Cerrar</button>
          <button className="qrm-btn qrm-btn--secondary" onClick={handleDownload} disabled={!!busy}>
            {busy === 'download' ? '…' : '⬇️ PDF'}
          </button>
          <button className="qrm-btn qrm-btn--primary" onClick={handlePrint} disabled={!!busy}>
            {busy === 'print' ? '…' : '🖨️ Imprimir PDF'}
          </button>
        </div>
      </div>

      {reprintOpen && (
        <ReprintDamagedModal
          open={reprintOpen}
          onClose={() => setReprintOpen(false)}
          lotId={currentLot?._id}
          loteNumero={currentLot?.loteNumero}
          productName={name}
          variationLabel={varLabel}
          sku={sku}
          price={price}
          storeName={storeName}
          productId={productId}
          variationId={variationId}
          labelConfig={labelConfig}
        />
      )}
    </div>
  );

  return createPortal(modal, document.body);
}

export default QRModal;
